import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, fmtINR } from "@/lib/api";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const ALLOWED_ROLES = new Set([
  "admin", "director", "finance", "accounts",
  "ho_approver", "project_head", "centre_coordinator",
]);

export default function PnL() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [drill, setDrill] = useState(null);
  const [drillBusy, setDrillBusy] = useState(false);
  const [projectFilter, setProjectFilter] = useState("all");
  useEffect(() => {
    if (user && ALLOWED_ROLES.has(user.role)) {
      api.get("/dashboard/centre-summary").then((r) => setRows(r.data));
    }
  }, [user]);
  if (user && !ALLOWED_ROLES.has(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  const filteredRows = rows.filter(
    (r) => projectFilter === "all" || (r.project_name || "Unassigned") === projectFilter
  );
  const totalIncome = filteredRows.reduce((s, r) => s + r.income, 0);
  const totalExpense = filteredRows.reduce((s, r) => s + r.expense, 0);
  const totalAssets = filteredRows.reduce((s, r) => s + (r.assets || 0), 0);

  const openDrill = async (centre_id) => {
    setDrillBusy(true);
    try {
      const r = await api.get(`/dashboard/centre-drilldown/${centre_id}`);
      setDrill(r.data);
    } finally {
      setDrillBusy(false);
    }
  };
  return (
    <div className="p-8 space-y-6" data-testid="pnl-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Financials</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Centre-wise Profit & Loss</h1>
        </div>
        <div className="flex items-center gap-2" data-testid="pnl-project-filter">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Project</span>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="h-10 border border-zinc-300 rounded-sm text-sm px-3 bg-white min-w-[200px]"
            data-testid="pnl-project-filter-select"
          >
            <option value="all">All Projects</option>
            {Array.from(new Set(rows.map((r) => r.project_name || "Unassigned"))).sort().map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <div className="p-6 bg-white border border-zinc-200"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Income</div><div className="text-3xl font-heading mt-2 tabular-nums text-emerald-700">{fmtINR(totalIncome)}</div></div>
        <div className="p-6 bg-white border border-zinc-200"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Expense</div><div className="text-3xl font-heading mt-2 tabular-nums">{fmtINR(totalExpense)}</div></div>
        <div className="p-6 bg-white border border-zinc-200"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total Assets</div><div className="text-3xl font-heading mt-2 tabular-nums text-zinc-800">{fmtINR(totalAssets)}</div></div>
        <div className="p-6 bg-white border border-zinc-200"><div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Consolidated P/L</div><div className={`text-3xl font-heading mt-2 tabular-nums ${totalIncome - totalExpense >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(totalIncome - totalExpense)}</div></div>
      </div>

      <div className="bg-white border border-zinc-200 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">Income vs Expense by Centre</div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={filteredRows}>
            <CartesianGrid stroke="#E4E4E7" strokeDasharray="3 3" />
            <XAxis dataKey="code" stroke="#71717A" fontSize={11} />
            <YAxis stroke="#71717A" fontSize={11} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
            <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ border: "1px solid #00414E", borderRadius: 0, background: "#fff" }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="income" fill="#059669" />
            <Bar dataKey="expense" fill="#00414E" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Code</th>
              <th className="text-left p-4">Centre</th>
              <th className="text-left p-4">Project</th>
              <th className="text-right p-4">Income</th>
              <th className="text-right p-4">Expense</th>
              <th className="text-right p-4">Assets</th>
              <th className="text-right p-4">Profit / Loss</th>
              <th className="text-right p-4">Annual Budget</th>
              <th className="text-right p-4">Utilisation</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {filteredRows.map((r) => (
              <tr key={r.centre_id}
                onClick={() => openDrill(r.centre_id)}
                data-testid={`pnl-row-${r.code}`}
                className="border-t border-zinc-100 hover:bg-emerald-50 cursor-pointer">
                <td className="p-4 font-mono text-xs">{r.code}</td>
                <td className="p-4 font-medium text-emerald-700 underline">{r.name}</td>
                <td className="p-4 text-zinc-600">{r.project_name || <span className="text-zinc-400">Unassigned</span>}</td>
                <td className="p-4 text-right">{fmtINR(r.income)}</td>
                <td className="p-4 text-right">{fmtINR(r.expense)}</td>
                <td className="p-4 text-right text-zinc-700">{fmtINR(r.assets || 0)}</td>
                <td className={`p-4 text-right font-medium ${r.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(r.profit)}</td>
                <td className="p-4 text-right">{fmtINR(r.annual_budget)}</td>
                <td className="p-4 text-right">{r.utilisation}%</td>
              </tr>
            ))}
            {filteredRows.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-zinc-400">No centres for this project.</td></tr>
            )}
          </tbody>
        </table>
        <div className="p-3 text-[10px] uppercase tracking-widest text-zinc-500 border-t border-zinc-100">
          Tip — click any centre row to open a category-wise drill-down report
        </div>
      </div>

      {/* Drill-down modal (spec §22) */}
      {drill && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDrill(null)} data-testid="pnl-drilldown">
          <div className="bg-white max-w-3xl w-full max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-baseline justify-between border-b border-zinc-200 pb-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Centre drill-down</div>
                <h2 className="font-heading text-2xl">{drill.centre.name}</h2>
                <div className="text-xs text-zinc-500">{drill.centre.code}</div>
              </div>
              <button onClick={() => setDrill(null)} className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black" data-testid="pnl-drilldown-close">Close ✕</button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="p-4 border border-zinc-200 bg-emerald-50">
                <div className="text-[10px] uppercase tracking-widest text-emerald-800">Total Income</div>
                <div className="font-heading text-2xl tabular-nums text-emerald-800" data-testid="drilldown-total-income">{fmtINR(drill.total_income || 0)}</div>
                <div className="text-xs text-emerald-700 mt-1">{drill.income_count || 0} entries · Received: {fmtINR(drill.total_received || 0)}</div>
              </div>
              <div className="p-4 border border-zinc-200 bg-zinc-50">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Expenses</div>
                <div className="font-heading text-2xl tabular-nums" data-testid="drilldown-total-expenses">{fmtINR(drill.total_expenses)}</div>
                <div className="text-xs text-zinc-500 mt-1">{drill.expense_count} entries</div>
              </div>
              <div className="p-4 border border-zinc-200 bg-zinc-50">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total Assets</div>
                <div className="font-heading text-2xl tabular-nums" data-testid="drilldown-total-assets">{fmtINR(drill.total_assets)}</div>
                <div className="text-xs text-zinc-500 mt-1">{drill.asset_count} items</div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-2">Income by Category</div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-400">
                    <tr><th className="text-left py-1">Category</th><th className="text-right py-1">#</th><th className="text-right py-1">Amount</th></tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {(drill.income_categories || []).length === 0 && <tr><td colSpan={3} className="py-3 text-zinc-400 text-center">No posted income.</td></tr>}
                    {(drill.income_categories || []).map((c) => (
                      <tr key={`inc-${c.category}`} className="border-t border-zinc-100">
                        <td className="py-2">{c.category}</td>
                        <td className="py-2 text-right text-zinc-500">{c.count}</td>
                        <td className="py-2 text-right font-medium text-emerald-700">{fmtINR(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Expenses by Category</div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-400">
                    <tr><th className="text-left py-1">Category</th><th className="text-right py-1">#</th><th className="text-right py-1">Amount</th></tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {drill.expense_categories.length === 0 && <tr><td colSpan={3} className="py-3 text-zinc-400 text-center">No approved expenses.</td></tr>}
                    {drill.expense_categories.map((c) => (
                      <tr key={`exp-${c.category}`} className="border-t border-zinc-100">
                        <td className="py-2">{c.category}</td>
                        <td className="py-2 text-right text-zinc-500">{c.count}</td>
                        <td className="py-2 text-right font-medium">{fmtINR(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Assets by Category</div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-widest text-zinc-400">
                    <tr><th className="text-left py-1">Category</th><th className="text-right py-1">#</th><th className="text-right py-1">Amount</th></tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {drill.asset_categories.length === 0 && <tr><td colSpan={3} className="py-3 text-zinc-400 text-center">No approved assets.</td></tr>}
                    {drill.asset_categories.map((c) => (
                      <tr key={`ast-${c.category}`} className="border-t border-zinc-100">
                        <td className="py-2">{c.category}</td>
                        <td className="py-2 text-right text-zinc-500">{c.count}</td>
                        <td className="py-2 text-right font-medium">{fmtINR(c.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {drillBusy && <div className="text-center text-zinc-400 mt-4">Loading…</div>}
          </div>
        </div>
      )}
    </div>
  );
}
